import { Fragment } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { LockedCard } from "@/components/layout/locked-content";

import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";
import { message, cn } from "@/lib/utils";
import { ConsumerResearchCollectionPerformanceData } from "@/schema/brand-os/ai-consumer-research";

function PerformanceComponent({ status, data }: { status: QueryStatus; data?: ConsumerResearchCollectionPerformanceData }) {
  switch (status) {
    case "pending":
      return <PerformanceCardSkeleton />;
    case "error":
      return <LockedCard message={message} className="w-full" />;
    case "success":
      return data ? <PerformanceContent data={data} /> : <LockedCard message={message} className="w-full" />;
  }
}

export function SubBrandPerformance() {
  const { data, status } = useQuery(ConsumerResearchApiFactory.Queries.CollectionPerformance());

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <p className="text-base font-semibold">Sub-Brand Performance</p>
        <p className="text-sm text-muted-foreground">Comparative analysis across linked collections</p>
      </div>
      <div className="flex overflow-x-auto scrollbar-hidden gap-6">
        <PerformanceComponent status={status} data={data} />
      </div>
    </div>
  );
}

function PerformanceContent({ data }: { data: ConsumerResearchCollectionPerformanceData }) {
  const collections = data?.collections ?? [];
  const maxMentions = Math.max(...collections.map((item) => item.total_mentions), 0);

  return (
    <Fragment>
      {collections.map((item) => {
        const progressValue = maxMentions > 0 ? (item.total_mentions / maxMentions) * 100 : 0;
        const isHighNps = item.nps >= 60;
        const isLowNps = item.nps < 58;
        const npsColor = isHighNps ? "text-success" : isLowNps ? "text-destructive" : "text-warning";
        const shareText = `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(item.share_of_mentions)}%`;

        return (
          <Card key={item.collection_name} className="flex flex-col min-w-64 basis-0">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-foreground">{item.collection_name}</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(item.total_mentions)}</p>
                <p className="text-xs text-muted-foreground">Total Mentions</p>
              </div>
              <Progress value={progressValue} className="h-1.5 bg-muted" indicatorClassName="rounded-full bg-destructive" />
              <div className="flex justify-between gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">NPS</p>
                  <p className={cn("text-lg font-semibold", npsColor)}>{item.nps}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Mentions</p>
                  <p className="text-sm font-medium text-foreground">{shareText}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </Fragment>
  );
}

function PerformanceCardSkeleton() {
  return (
    <Fragment>
      {Array.from({ length: 4 }, (_, i) => (
        <Card key={i} className="flex flex-col gap-3 py-5 min-w-64 flex-1 basis-0">
          <CardContent className="flex flex-col gap-3 px-5 pt-0">
            <Skeleton className="h-4 w-24 rounded-md" />
            <Skeleton className="h-8 w-20 rounded-md" />
            <div className="flex items-center gap-4">
              <Skeleton className="h-3 w-14 rounded-md" />
              <Skeleton className="h-3 w-20 rounded-md" />
            </div>
            <div className="h-28 w-full bg-muted rounded-md" />
          </CardContent>
        </Card>
      ))}
    </Fragment>
  );
}
